// src/components/ProfileComponent.js

import { useState, useContext } from 'react';
import { Avatar } from '@mui/material';
import { useAuthContext } from '../../store/Contexts';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ModalContext, ModalTypes } from '../../store/Contexts';

import './ProfileComponent.scss';
import { LogEventType, UserType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const ProfileComponent = () => {
    const [open, setOpen] = useState(false);
    const {
        user,
        logout,
        allowAdditionalUser,
        adminView,
        multitenantFlag,
        setAdminView,
    } = useAuthContext();
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();
    const ind = user?.jwt?.ind;
    const tenant = user?.jwt?.tenant;
    const tenantId = window.localStorage.getItem('tenantId');
    const allowViewSwitch = tenant == tenantId;
    const isAdmin =
        ind &&
        allowViewSwitch &&
        !multitenantFlag &&
        user?.jwt?.type <= UserType.ClientAdmin;

    const CloseIcon = () => (
        <div className="flex-align-center">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 7 7"
                fill="none"
                aria-hidden="true"
                focusable="false"
                className="svg-icon-inline"
            >
                <path
                    d="M0.146447 0.146447C0.341709 -0.0488155 0.658291 -0.0488155 0.853553 0.146447L3.5 2.79289L6.14645 0.146447C6.34171 -0.0488155 6.65829 -0.0488155 6.85355 0.146447C7.04882 0.341709 7.04882 0.658291 6.85355 0.853553L4.20711 3.5L6.85355 6.14645C7.04882 6.34171 7.04882 6.65829 6.85355 6.85355C6.65829 7.04882 6.34171 7.04882 6.14645 6.85355L3.5 4.20711L0.853553 6.85355C0.658291 7.04882 0.341709 7.04882 0.146447 6.85355C-0.0488155 6.65829 -0.0488155 6.34171 0.146447 6.14645L2.79289 3.5L0.146447 0.853553C-0.0488155 0.658291 -0.0488155 0.341709 0.146447 0.146447Z"
                    fill="#667085"
                />
            </svg>
            <span className="close-icon-text">
                {getText('msg_bbfa773e5a63')}
            </span>
        </div>
    );

    const handleLogout = () => {
        setOpen(false);
        Promise.resolve(logout())
            .catch(err => {
                console.error('Logout failed:', err);
            })
            .finally(() => {
                setTimeout(() => {
                    window.location.reload();
                }, 250);
            });
    };

    const handleSetAdminView = admin => {
        setOpen(false);
        setAdminView(admin);
        navigate(ROUTES.ROOT);
        logEvent(
            LogEventType.AdminIndividualViewChanged,
            `User transitioned from ${admin ? 'individual' : 'admin'} to ${admin ? 'admin' : 'individual'} view`,
        );
    };

    function renderViewSwitch() {
        if (!isAdmin) {
            return null;
        }

        return (
            <div
                className="child-container-mobile-profile cursor-pointer"
                onClick={() => handleSetAdminView(!adminView)}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        handleSetAdminView(!adminView);
                    }
                }}
            >
                <div>
                    <i className="bi bi-arrow-left-right menu-icon menu-icon-large" />

                    <span className="menu-text">
                        {adminView
                            ? 'Switch to Individual View'
                            : 'Switch to Admin View'}
                    </span>
                </div>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="17"
                    viewBox="0 0 7 12"
                    fill="none"
                >
                    <path
                        d="M1 10.6812L5.5 5.84057L1 0.999988"
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
        );
    }

    return (
        <div className="white-bg-bottom-padding">
            <div className="display-flex justify-content-end p-3">
                <Link
                    to={ROUTES.ROOT}
                    className="cursor-pointer text-link-no-decoration"
                >
                    <CloseIcon />
                </Link>
            </div>

            <div className="centered-container">
                <Avatar
                    className="align-self-center header-avatar-mobile"
                    src={user.avatar_url ?? ''}
                />
                <span>{user.first_name + ' ' + user.last_name}</span>
            </div>

            <div className="parent-container-mobile-profile">
                <NavLink
                    to={ROUTES.USER_INFO}
                    className="cursor-pointer text-decoration-none"
                >
                    <div className="child-container-mobile-profile">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                className="menu-icon"
                            >
                                <path
                                    d="M12 12C15.3137 12 18 9.31371 18 6C18 2.68629 15.3137 0 12 0C8.68629 0 6 2.68629 6 6C6 9.31371 8.68629 12 12 12ZM16 6C16 8.20914 14.2091 10 12 10C9.79086 10 8 8.20914 8 6C8 3.79086 9.79086 2 12 2C14.2091 2 16 3.79086 16 6Z"
                                    fill="black"
                                />
                                <path
                                    d="M24 22C24 24 22 24 22 24H2C2 24 0 24 0 22C0 20 2 14 12 14C22 14 24 20 24 22ZM22 21.993C21.9971 21.4995 21.6924 20.0209 20.3358 18.6642C19.0313 17.3597 16.5782 16 12 16C7.42176 16 4.96871 17.3597 3.66419 18.6642C2.30751 20.0209 2.00285 21.4995 2 21.993H22Z"
                                    fill="black"
                                />
                            </svg>

                            <span className="menu-text">
                                {getText('msg_ff4fc0276e96')}
                            </span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="17"
                            viewBox="0 0 7 12"
                            fill="none"
                        >
                            <path
                                d="M1 10.6812L5.5 5.84057L1 0.999988"
                                stroke="#2F2B43"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </NavLink>

                <NavLink
                    to={ROUTES.PASSWORD}
                    className="cursor-pointer text-decoration-none"
                >
                    <div className="child-container-mobile-profile">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                className="menu-icon"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 22.3995C12.0113 22.3979 12.0588 22.3911 12.1511 22.3625C12.2633 22.3278 12.4109 22.2705 12.5914 22.1855C12.952 22.0158 13.4109 21.7511 13.93 21.3851C14.9673 20.6536 16.2115 19.5409 17.3615 18.0364C19.652 15.04 21.5717 10.4893 20.7409 4.25458C20.7054 3.98875 20.5168 3.75613 20.2482 3.66847C19.2718 3.34987 17.6221 2.82783 15.9931 2.38626C14.3275 1.93478 12.7963 1.6 12 1.6V22.3995ZM7.60748 0.839479C9.23608 0.398015 10.9658 0 12 0C13.0342 0 14.7639 0.398015 16.3925 0.839479C18.0577 1.29085 19.7353 1.82191 20.7221 2.14392C21.5456 2.41265 22.1685 3.14039 22.2882 4.03814C23.1831 10.7541 21.1079 15.7303 18.591 19.023C17.3373 20.6631 15.9746 21.8855 14.8154 22.7031C14.2361 23.1115 13.6993 23.4248 13.2434 23.6394C12.8222 23.8377 12.3716 24 12 24C11.6284 24 11.1778 23.8377 10.7566 23.6394C10.3007 23.4248 9.76386 23.1115 9.18464 22.7031C8.02536 21.8855 6.66274 20.6631 5.409 19.023C2.89206 15.7303 0.816912 10.7541 1.71185 4.03814C1.83148 3.14039 2.45438 2.41265 3.27792 2.14393C4.26474 1.82191 5.94234 1.29085 7.60748 0.839479Z"
                                    fill="black"
                                />
                            </svg>

                            <span className="menu-text">
                                {getText('msg_8be3c943b160')}
                            </span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="17"
                            viewBox="0 0 7 12"
                            fill="none"
                        >
                            <path
                                d="M1 10.6812L5.5 5.84057L1 0.999988"
                                stroke="#2F2B43"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </NavLink>

                <NavLink
                    to={ROUTES.COMMUNICATION_PREFERENCES}
                    className="cursor-pointer text-decoration-none"
                >
                    <div className="child-container-mobile-profile">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="black"
                                className="bi bi-envelope menu-icon"
                                viewBox="0 0 16 16"
                            >
                                <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z" />
                            </svg>

                            <span className="menu-text">
                                {getText('msg_690476370b42')}
                            </span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="17"
                            viewBox="0 0 7 12"
                            fill="none"
                        >
                            <path
                                d="M1 10.6812L5.5 5.84057L1 0.999988"
                                stroke="#2F2B43"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </NavLink>

                {ind && allowAdditionalUser && !adminView && (
                    <NavLink
                        to="/additional-users"
                        className="cursor-pointer text-decoration-none"
                    >
                        <div className="child-container-mobile-profile">
                            <div>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="menu-icon"
                                >
                                    <path
                                        d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z"
                                        fill="black"
                                    />
                                </svg>
                                <span className="menu-text">
                                    {getText('msg_d31e71815f1e')}
                                </span>
                            </div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="17"
                                viewBox="0 0 7 12"
                                fill="none"
                            >
                                <path
                                    d="M1 10.6812L5.5 5.84057L1 0.999988"
                                    stroke="#2F2B43"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </NavLink>
                )}

                <div
                    onClick={() => showModal({ type: ModalTypes.INSTALLATION })}
                    className="cursor-pointer text-decoration-none"
                >
                    <div className="child-container-mobile-profile">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 16 16"
                                fill="black"
                                className="bi bi-phone menu-icon"
                            >
                                <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                                <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
                            </svg>

                            <span className="menu-text">
                                {getText('msg_ad0bb58de6ed')}
                            </span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="17"
                            viewBox="0 0 7 12"
                            fill="none"
                        >
                            <path
                                d="M1 10.6812L5.5 5.84057L1 0.999988"
                                stroke="#2F2B43"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </div>

                {/* <NavLink
                    to={ROUTES.FAQS}
                    className="cursor-pointer text-decoration-none"
                >
                    <div className="child-container-mobile-profile">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                style={{ marginRight: '24px' }}
                            >
                                <path
                                    d="M12 22.5C6.20101 22.5 1.5 17.799 1.5 12C1.5 6.20101 6.20101 1.5 12 1.5C17.799 1.5 22.5 6.20101 22.5 12C22.5 17.799 17.799 22.5 12 22.5ZM12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z"
                                    fill="black"
                                />
                                <path
                                    d="M7.88267 8.67922C7.87128 8.88355 8.03986 9.04907 8.2445 9.04907H9.48123C9.68834 9.04907 9.85302 8.88019 9.88084 8.67496C10.0143 7.69077 10.6898 6.97339 11.8938 6.97339C12.9221 6.97339 13.8648 7.48755 13.8648 8.72534C13.8648 9.67749 13.303 10.1155 12.4175 10.782C11.4082 11.5151 10.6084 12.3721 10.6656 13.7622L10.67 14.0874C10.6728 14.2925 10.8399 14.4573 11.045 14.4573H12.2615C12.4686 14.4573 12.6365 14.2894 12.6365 14.0823V13.9241C12.6365 12.8481 13.0459 12.5339 14.1504 11.696C15.0645 11.001 16.0166 10.2297 16.0166 8.61108C16.0166 6.34497 14.1028 5.25 12.0081 5.25C10.1075 5.25 8.02446 6.13592 7.88267 8.67922ZM10.218 17.3232C10.218 18.123 10.856 18.7134 11.732 18.7134C12.646 18.7134 13.2744 18.123 13.2744 17.3232C13.2744 16.4949 12.646 15.9141 11.732 15.9141C10.856 15.9141 10.218 16.4949 10.218 17.3232Z"
                                    fill="black"
                                />
                            </svg>
                            <span style={{ color: 'black' }}>FAQs</span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="17"
                            viewBox="0 0 7 12"
                            fill="none"
                        >
                            <path
                                d="M1 10.6812L5.5 5.84057L1 0.999988"
                                stroke="#2F2B43"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </NavLink> */}

                <NavLink
                    to={ROUTES.PRIVACY_POLICY}
                    className="cursor-pointer text-decoration-none"
                >
                    <div className="child-container-mobile-profile">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="24"
                                viewBox="0 0 16 24"
                                fill="none"
                                className="menu-icon privacy-icon-offset"
                            >
                                <path
                                    d="M8.5 1.5C10.1569 1.5 11.5 2.84315 11.5 4.5V10.5H5.5V4.5C5.5 2.84315 6.84315 1.5 8.5 1.5ZM13 10.5V4.5C13 2.01472 10.9853 0 8.5 0C6.01472 0 4 2.01472 4 4.5V10.5C2.34315 10.5 1 11.8431 1 13.5V21C1 22.6569 2.34315 24 4 24H13C14.6569 24 16 22.6569 16 21V13.5C16 11.8431 14.6569 10.5 13 10.5ZM4 12H13C13.8284 12 14.5 12.6716 14.5 13.5V21C14.5 21.8284 13.8284 22.5 13 22.5H4C3.17157 22.5 2.5 21.8284 2.5 21V13.5C2.5 12.6716 3.17157 12 4 12Z"
                                    fill="black"
                                />
                            </svg>
                            <span className="menu-text">
                                {getText('msg_9db108ba6b7f')}
                            </span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="17"
                            viewBox="0 0 7 12"
                            fill="none"
                        >
                            <path
                                d="M1 10.6812L5.5 5.84057L1 0.999988"
                                stroke="#2F2B43"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </NavLink>

                {ind && (
                    <NavLink
                        to={ROUTES.RESOURCES}
                        state={{ individualResourceScope: true }}
                        className="cursor-pointer text-decoration-none"
                    >
                        <div className="child-container-mobile-profile">
                            <div>
                                <i className="bi bi-question-circle menu-icon menu-icon-large" />
                                <span className="menu-text">
                                    {getText('msg_05b180520b4b')}
                                </span>
                            </div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="17"
                                viewBox="0 0 7 12"
                                fill="none"
                            >
                                <path
                                    d="M1 10.6812L5.5 5.84057L1 0.999988"
                                    stroke="#2F2B43"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </NavLink>
                )}

                <NavLink
                    to={ROUTES.CONTACT_SUPPORT}
                    className="cursor-pointer text-decoration-none"
                >
                    <div className="child-container-mobile-profile">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                className="menu-icon"
                            >
                                <path
                                    d="M0 5C0 3.34315 1.34315 2 3 2H21C22.6569 2 24 3.34315 24 5V17C24 18.6569 22.6569 20 21 20H3C1.34315 20 0 18.6569 0 17V5ZM3 3.5C2.17157 3.5 1.5 4.17157 1.5 5V5.32536L12 11.6254L22.5 5.32536V5C22.5 4.17157 21.8284 3.5 21 3.5H3ZM22.5 7.07464L15.4379 11.3119L22.5 15.6578V7.07464ZM22.4494 17.3879L13.9887 12.1814L12 13.3746L10.0113 12.1814L1.55065 17.3879C1.72162 18.0283 2.30572 18.5 3 18.5H21C21.6943 18.5 22.2784 18.0283 22.4494 17.3879ZM1.5 15.6578L8.56211 11.3119L1.5 7.07464V15.6578Z"
                                    fill="black"
                                />
                            </svg>

                            <span className="menu-text">
                                {getText('msg_fe1a2922069b')}
                            </span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="17"
                            viewBox="0 0 7 12"
                            fill="none"
                        >
                            <path
                                d="M1 10.6812L5.5 5.84057L1 0.999988"
                                stroke="#2F2B43"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </NavLink>
                {renderViewSwitch()}
            </div>
            <br />
            <div className="centered-container">
                <button
                    className="ing-button-primary-none"
                    type="submit"
                    onClick={handleLogout}
                >
                    {getText('msg_e43d612e11f1')}
                </button>
            </div>
        </div>
    );
};

export default ProfileComponent;
